import React, { useEffect, useState } from 'react';
import { MENU_ITEMS } from './constants';

interface MenuArcProps {
  isOpen: boolean;
  onItemClick?: (id: string) => void;
}

export const MenuArc: React.FC<MenuArcProps> = ({ isOpen, onItemClick }) => {
  const [renderItems, setRenderItems] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setRenderItems(true);
    } else {
      // Delay unmounting to allow exit animation
      const timer = setTimeout(() => setRenderItems(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!renderItems && !isOpen) return null;

  return (
    <div className="absolute bottom-0 left-0 w-full h-full pointer-events-none z-40">
      {MENU_ITEMS.map((item, index) => {
        const isLarge = item.size === 'large';
        
        // Calculate the translation needed to move the item back to the center (under the button)
        // Even indexes are on the LEFT (need +X to reach center)
        // Odd indexes are on the RIGHT (need -X to reach center)
        const isLeft = index % 2 === 0;
        
        // Distances correspond to constants.ts offsets: 50, 88, 126
        const dist = index < 2 ? 50 : index < 4 ? 88 : 126;
        const closedTranslateX = isLeft ? `${dist}px` : `-${dist}px`;

        return (
          <button
            key={item.id}
            onClick={() => onItemClick && onItemClick(item.id)}
            className={`
              absolute ${item.positionClass}
              ${isLarge ? 'w-[50px] h-[50px]' : 'w-10 h-10'} 
              rounded-full flex items-center justify-center text-white
              transition-all duration-500 cubic-bezier(0.34, 1.56, 0.64, 1)
              pointer-events-auto group
              ${isOpen ? 'opacity-100' : 'opacity-0'}
            `}
            style={{ 
                // Dynamic transform for the "fan out" effect
                transform: isOpen 
                    ? 'translate(0, 0) scale(1)' 
                    : `translate(${closedTranslateX}, 10px) scale(0.5)`,
                
                transitionDelay: `${isOpen ? index * 30 : 0}ms`,
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                zIndex: 40 - index // Ensure inner items stack on top of outer ones if they overlap slightly
            }}
          >
             <span className={`material-symbols-outlined ${isLarge ? 'text-[24px]' : 'text-[20px]'} drop-shadow-sm`}>
               {item.icon}
             </span>

             {/* Label tooltip */}
             <span className="absolute -top-8 text-xs font-medium text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-md tracking-wide whitespace-nowrap bg-black/40 px-2 py-1 rounded-full backdrop-blur-sm pointer-events-none">
               {item.label}
             </span>

             {/* Ring for profile picture if large */}
             {isLarge && (
                <div className="absolute inset-0 rounded-full border border-white/30 pointer-events-none" />
             )}
          </button>
        );
      })}
    </div>
  );
};